import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  FaSearch,
  FaPlus,
  FaSort,
  FaChevronDown,
  FaFilter,
  FaStore,
  FaUser,
  FaEllipsisH,
} from "react-icons/fa";
import Button from "../../../components/MaterialButton";
import Input from "../../../components/MaterialInput";
import Card from "../../../components/MaterialCard";
import { BASE_URL } from "utils/constant";
import VendorShopManagement from "./Vendor/VendorShopManagement";
import InventoryItemDetails from "./Inventory/InventoryItemDetails";
import NewMaterialForm from "./Inventory/NewMaterialForm";
import DeliveryCard from "./Delivery/DeliveryCard";
import VendorProfile from "./Vendor/VendorProfile";
import DeliveryStatusFilter from "./Delivery/DeliveryStatusFilter";
import MaterialPurchaseForm from "../Finance/MaterialPurchaseForm";
import NewVendorModal from "./Vendor/NewVendor";
import materialIcons from "utils/materialIcons";
import { GoArrowSwitch } from "react-icons/go";
import { useAlert } from "components/UI_components/Alert";
import { display } from "@mui/system";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";
import DeliverySortFilter from "./Delivery/DeliverySortFilter";
import InventorySortFilter from "./Inventory/InventorySortFilter";
import { Loader } from "lucide-react";
import { json } from "utils/http/client";
import EmptyIcon from "assets/empty state/NoData.svg";
import MaterialCreateForm from "./Inventory/MultiCreateMaterialForm";
import NewMaterialRequest from "./MaterialRequest/NewRequest";
import MaterialRequestSidebar from "./MaterialRequest/MaterialRequestSidebar";
import DeliveryDetails from "./Delivery/DeliveryDetails";
import { SiDatabricks } from "react-icons/si";
import LoadingScreen from "components/UI_components/LoadingScreen";
import StockUpdateForm from "./Inventory/StockUpdateforTask";

const InventoryItem = ({ item, onClick, onUpdateClick, index, length }) => (
  <div
    className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 ${index === length - 1 ? "" : "border-b border-gray-200"
      }`}
    onClick={() => onClick(item)}
  >
    <div className="flex items-center space-x-4 w-1/3">
      {materialIcons[item.material_name.toLowerCase()] ? (
        <img
          src={materialIcons[item.material_name.toLowerCase()]}
          alt={item.material_name}
          className="w-12 h-12 rounded-full"
        />
      ) : (
        <div className="w-12 h-12 rounded-full flex items-center justify-center">
          <SiDatabricks className="text-sm-primary" size={28} />
        </div>
      )}
      <div className="flex flex-col">
        <h3 className="font-semibold">{item.material_name}</h3>
        <p className="text-sm text-gray-500">{item.subcategory}</p>
      </div>
    </div>
    <div className="flex items-center justify-between w-2/3">
      <div className="text-left w-1/3">
        <p className="text-sm text-gray-500">In stock:</p>
        <p className="font-semibold">
          {item.current_stock} {item.uom}
        </p>
      </div>
      <div className="text-right">
        <p className="text-sm text-gray-500">
          Updated {formatDate(item.last_updated_on)}
        </p>
        {item.created_by && (
          <p className="text-xs text-gray-500">Created by: {item.created_by}</p>
        )}
        {item.last_updated_by && (
          <p className="text-xs text-gray-500">Last updated by: {item.last_updated_by}</p>
        )}
        <p
          className={`text-sm font-semibold ${item.added_stock > item.used_stock
            ? "text-green-500"
            : "text-red-500"
            }`}
        >
          {item.added_stock > item.used_stock
            ? `+${item.added_stock}`
            : `${item.used_stock}`}{" "}
          {item.uom}
        </p>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onUpdateClick(item); }}
        className="bg-[#ECE6F0] text-text-clr font-bold px-4 py-2 rounded-md flex flex-row items-center gap-2 justify-center hover:bg-purple-200 text-sm">
        Update
        <div className="rounded-full bg-[#C5B3D2] p-2">
          <GoArrowSwitch fill="#3B0069" />
        </div>
      </button>
    </div>
  </div>
);

const VendorItem = ({ vendor, onClick }) => (
  <div
    className="w-full p-4 mb-2 bg-white rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:bg-gray-50"
    onClick={() => onClick?.(vendor)}
  >
    <div className="flex items-center pb-2 border-b border-gray-200">
      <div className="p-2 bg-gray-100 rounded-full mr-3">
        {vendor.vendor_type === "Shop" ? (
          <FaStore size={28} className="text-purple-700" />
        ) : (
          <FaUser size={28} className="text-purple-700" />
        )}
      </div>

      <div className="flex-grow">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{vendor.name}</h3>
            <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium text-gray-600">
              {vendor.vendor_type}
            </span>
          </div>
          <button className="hover:bg-gray-100 p-1 rounded-full">
            <FaEllipsisH size={20} className="text-gray-700" />
          </button>
        </div>
        <p className="text-sm font-medium text-gray-600 mt-0.5">
          +91- {vendor.contact_no}
        </p>
      </div>
    </div>

    <div className="flex justify-between items-center mt-2">
      <div>
        <p className="text-xs font-medium text-gray-600">
          Last Purchase/Delivery
        </p>
        <p className="text-xs text-gray-600">
          {formatDate(vendor.last_updated_on)}
        </p>
      </div>
      <div>
        <p className="text-xs font-medium text-gray-600">Upcoming Delivery</p>
        <p className="text-xs text-gray-600">
          {formatDate(vendor.last_updated_on)}
        </p>
      </div>
    </div>
  </div>
);

const MaterialRequestItem = ({ request, onClick, activeRequestTab }) => (
  <div
    className="w-full px-3 py-2 mb-2 bg-white grid grid-cols-[1.2fr_2fr_1.6fr_1.6fr_auto] gap-4 items-center rounded-lg shadow-sm border-[1px] border-[#CCCCCC] cursor-pointer hover:bg-gray-50"
    onClick={() => onClick(request)}
  >
    <div className="flex flex-row items-center text-[#232323] font-normal">
      {request.mr_number}
    </div>
    <div className="flex flex-col items-start">
      <div className="text-[#737373] text-sm">Content</div>
      <div>
        {request.material_request_lines[0]?.material_name}
        {request.material_request_lines.length > 1
          ? ` + ${request.material_request_lines.length - 1} items`
          : ""}
      </div>
    </div>
    <div className="flex flex-col items-start">
      <div className="flex items-center gap-1 whitespace-nowrap">
        <span className="text-[#737373] text-sm">
          {activeRequestTab === "Pending" ? "Created by" : "Approved by"}
        </span>
        <span>{request.last_updated_by || "N/A"}</span>
      </div>

      <div>
        <span className="text-[#737373] text-sm">On </span>
        {activeRequestTab === "Pending"
          ? formatDMY(request.created_on)
          : formatDMY(request.last_updated_on)}
      </div>
    </div>
    <div className="flex flex-col items-start">
      <div className="text-[#737373] text-sm">Delivery expected by</div>
      <div>
        {request.due_date
          ? new Date(request.due_date).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
          : "N/A"}
      </div>
    </div>
    <button
      onClick={() => onClick(request)}
      className="flex flex-row items-center justify-center text-sm text-[#3B0069] font-medium px-6 py-1 hover:bg-purple-50 rounded-md border-[1px] border-[#3B0069]"
    >
      View
    </button>
  </div>
);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
  });
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${formattedDate} ${formattedTime}`;
};

const formatDMY = (dateString) => {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const Inventory = ({
  userPermissions,
  setTab,
  setDelivery,
  delivery,
  permissionsLoading,
}) => {
  const { id: pathId, siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const tabQueryMap = useMemo(
    () => ({
      inventory: "inventory",
      purchase: "Purchases & Deliveries",
      requests: "material Requests",
      vendors: "vendors",
      machineries: "machineries",
    }),
    [],
  );

  const reverseTabQueryMap = useMemo(
    () => ({
      inventory: "inventory",
      "Purchases & Deliveries": "purchase",
      "material Requests": "requests",
      vendors: "vendors",
      machineries: "machineries",
    }),
    [],
  );

  const getActiveTabFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("activeTab");
    return tabQueryMap[tab] || "inventory";
  }, [location.search, tabQueryMap]);

  const [currentTab, setCurrentTab] = useState(getActiveTabFromUrl());
  const [inventoryItems, setInventoryItems] = useState(
    localStorage.getItem(`${siteId}-inventory`)
      ? JSON.parse(localStorage.getItem(`${siteId}-inventory`))
      : [],
  );
  const [deliveryItems, setDeliveryItems] = useState(
    localStorage.getItem(`${siteId}-deliveries`)
      ? JSON.parse(localStorage.getItem(`${siteId}-deliveries`))
      : [],
  );
  const [vendorItems, setVendorItems] = useState(
    localStorage.getItem(`${siteId}-vendors`)
      ? JSON.parse(localStorage.getItem(`${siteId}-vendors`))
      : [],
  );
  const [machineryItems, setMachineryItems] = useState(
    localStorage.getItem(`${siteId}-machineries`)
      ? JSON.parse(localStorage.getItem(`${siteId}-machineries`))
      : [],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [deliveryFilter, setDeliveryFilter] = useState("all");
  const [activeDeliveryStatus, setActiveDeliveryStatus] = useState("Upcoming");
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showNewMaterialForm, setShowNewMaterialForm] = useState(false);
  const [showNewDeliveryForm, setShowNewDeliveryForm] = useState(false);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [materialRequests, setMaterialRequests] = useState(
    localStorage.getItem(`${siteId}-materialRequests`)
      ? JSON.parse(localStorage.getItem(`${siteId}-materialRequests`))
      : [],
  );
  const [filteredMaterialRequests, setFilteredMaterialRequests] = useState([]);
  const [showMaterialPurchaseDetails, setShowMaterialPurchaseDetails] =
    useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [pendingCreatedRequestId, setPendingCreatedRequestId] = useState(null);
  // Default the material request tab to "Pending" so a page refresh always shows Pending
  const [activeRequestTab, setActiveRequestTab] = useState("Pending");
  const [materialRequestSortBy, setMaterialRequestSortBy] = useState("recently_added");
  const [isRequestSortOpen, setIsRequestSortOpen] = useState(false);
  const requestSortRef = useRef(null);
  const RequestTabs = [
    { label: "Pending", value: ["PENDING_APPROVAL"] },
    {
      label: "Approved",
      value: [
        "OPEN",
        "SENT",
        "COMPLETED",
        "PARTIAL_DELIVERY",
        "RETURN",
        "DRAFT",
      ],
    },
    { label: "Rejected", value: ["REJECTED"] },
  ];
  const materialRequestSortOptions = [
    { label: "Recently Added", value: "recently_added" },
    { label: "Expected Delivery Date", value: "expected_delivery" },
  ];
  const [stockUpdateItem, setStockUpdateItem] = useState(null);

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
  };

  const sortMaterialRequests = useCallback((requests, sortBy) => {
    const parseDate = (dateString, fallback = 0) => {
      const timestamp = new Date(dateString).getTime();
      return Number.isNaN(timestamp) ? fallback : timestamp;
    };

    if (sortBy === "expected_delivery") {
      return [...requests].sort((a, b) => {
        const dateA = a?.due_date
          ? parseDate(a.due_date, Number.POSITIVE_INFINITY)
          : Number.POSITIVE_INFINITY;
        const dateB = b?.due_date
          ? parseDate(b.due_date, Number.POSITIVE_INFINITY)
          : Number.POSITIVE_INFINITY;

        if (dateA !== dateB) {
          return dateA - dateB;
        }

        return parseDate(b?.created_on) - parseDate(a?.created_on);
      });
    }

    return [...requests].sort(
      (a, b) => parseDate(b?.created_on) - parseDate(a?.created_on)
    );
  }, []);

  const alert = useAlert();
  const fetchData = useCallback(async () => {
    if (currentTab === "inventory") {
      if (inventoryItems.length === 0) {
        setIsLoading(true);
      }
    }
    if (currentTab === "Purchases & Deliveries") {
      if (deliveryItems.length === 0) {
        setIsLoading(true);
      }
    }
    if (currentTab === "vendors") {
      if (vendorItems.length === 0) {
        setIsLoading(true);
      }
    }
    if (currentTab === "machineries") {
      if (machineryItems.length === 0) {
        setIsLoading(true);
      }
    }
    if (currentTab === "material Requests") {
      if (materialRequests.length === 0) {
        setIsLoading(true);
      }
    }

    try {
      const accessToken = localStorage.getItem("access");
      const site = localStorage.getItem("site");

      const endpoints = {
        inventory: "get-materials-of-a-site",
        "Purchases & Deliveries": "get-deliveries-of-a-site",
        vendors: "list-site-vendors",
        machineries: "get-machineries-of-a-site",
        "material Requests": `material-requests/site/${site}`,
      };

      const url = `${BASE_URL}/inventory/${endpoints[currentTab]}/`;

      // const response = await fetch(url, {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     Authorization: `Bearer ${accessToken.trim()}`,
      //   },
      //   body: JSON.stringify({ sites: site }),
      // });

      // if (!response.ok) {
      //   throw new Error(`HTTP error! Status: ${response.status}`);
      // }

      // const data = await response.json();
      const data = await json(url, {
        method: currentTab === "material Requests" ? "GET" : "POST",
        body: currentTab === "material Requests" ? null : { sites: site },
      });

      console.log(`Fetched ${currentTab} data:`, data);
      if (data && Array.isArray(data.data)) {
        switch (currentTab) {
          case "inventory":
            // Default sort: newest first by last_updated_on
            const sortedInventory = [...data.data].sort((a, b) => {
              return new Date(b.last_updated_on) - new Date(a.last_updated_on);
            });
            setInventoryItems(sortedInventory);
            localStorage.setItem(
              `${site}-inventory`,
              JSON.stringify(sortedInventory),
            );
            break;
          case "Purchases & Deliveries":
            // Sort deliveries by date (most recent first)
            const sortedDeliveries = [...data.data].sort((a, b) => {
              return new Date(b.date) - new Date(a.date);
            });
            setDeliveryItems(sortedDeliveries);
            localStorage.setItem(`${site}-deliveries`, JSON.stringify(sortedDeliveries));
            // Keep filtered list aligned with the selected delivery status tab
            const statusIdMap = {
              Upcoming: 1,
              Delivered: 2,
              Cancelled: 3,
            };
            const activeTransactionId =
              statusIdMap[activeDeliveryStatus] ?? statusIdMap.Upcoming;
            setFilteredDeliveries(
              sortedDeliveries.filter((d) => d.transaction_id === activeTransactionId)
            );
            break;
          case "vendors":
            setVendorItems(data.data);
            break;
          case "machineries":
            setMachineryItems(data.data);
            break;
          case "material Requests":
            const sortedRequests = [...data.data].sort(
              (a, b) => new Date(b.created_on) - new Date(a.created_on),
            );
            setMaterialRequests(sortedRequests);
            localStorage.setItem(
              `${site}-materialRequests`,
              JSON.stringify(sortedRequests),
            );
            break;
        }
      }
    } catch (error) {
      console.error(`Error fetching ${currentTab} data:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [currentTab, activeDeliveryStatus]);

  useEffect(() => {
    fetchData();
    setCurrentPage(1);
  }, [fetchData, currentTab]);

  // Sync tab with URL
  useEffect(() => {
    const tab = getActiveTabFromUrl();
    if (tab !== currentTab) {
      setCurrentTab(tab);
    }
  }, [getActiveTabFromUrl, currentTab]);

  // Handle deep linking to specific items
  useEffect(() => {
    if (pathId) {
      const accessToken = localStorage.getItem("access");
      const site = siteId || localStorage.getItem("site");
      const urlParams = new URLSearchParams(location.search);
      const activeTabParam = urlParams.get("activeTab");

      const fetchSpecificItem = async () => {
        setIsLoading(true);
        try {
          // If activeTab is purchase, try fetching as delivery first
          if (activeTabParam === "purchase") {
            const deliveryRes = await fetch(
              `${BASE_URL}/inventory/get-a-delivery/${pathId}/`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              },
            );
            const deliveryData = await deliveryRes.json();
            if (deliveryData.status) {
              setSelectedDelivery(deliveryData.data);
              setShowMaterialPurchaseDetails(true);
              setCurrentTab("Purchases & Deliveries");
              return;
            }
          }

          // Try fetching as material
          const materialRes = await fetch(
            `${BASE_URL}/inventory/get-a-material/${pathId}/`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            },
          );
          const materialData = await materialRes.json();
          if (materialData.status) {
            setSelectedItem(materialData.data);
            setCurrentTab("inventory");
            return;
          }

          // If not found as material and we haven't checked delivery yet, try delivery
          if (activeTabParam !== "purchase") {
            const deliveryRes = await fetch(
              `${BASE_URL}/inventory/get-a-delivery/${pathId}/`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              },
            );
            const deliveryData = await deliveryRes.json();
            if (deliveryData.status) {
              setSelectedDelivery(deliveryData.data);
              setShowMaterialPurchaseDetails(true);
              setCurrentTab("Purchases & Deliveries");
              return;
            }
          }
        } catch (e) {
          console.error("Error fetching deep linked item:", e);
        } finally {
          setIsLoading(false);
        }
      };

      // Check if already in loaded lists with priority based on activeTab
      if (activeTabParam === "purchase") {
        const foundDelivery = deliveryItems.find((d) => String(d.id) === pathId);
        if (foundDelivery) {
          setSelectedDelivery(foundDelivery);
          setShowMaterialPurchaseDetails(true);
          setCurrentTab("Purchases & Deliveries");
          return;
        }
      }

      const foundItem = inventoryItems.find((i) => String(i.id) === pathId);
      if (foundItem) {
        setSelectedItem(foundItem);
        setCurrentTab("inventory");
        return;
      }

      if (activeTabParam !== "purchase") {
        const foundDelivery = deliveryItems.find((d) => String(d.id) === pathId);
        if (foundDelivery) {
          setSelectedDelivery(foundDelivery);
          setShowMaterialPurchaseDetails(true);
          setCurrentTab("Purchases & Deliveries");
          return;
        }
      }

      fetchSpecificItem();
    } else {
      setSelectedItem(null);
      setSelectedDelivery(null);
      setShowMaterialPurchaseDetails(false);
    }
  }, [pathId, siteId, inventoryItems, deliveryItems, location.search]);

  // Keep filtered material requests in sync with active tab and fetched data
  useEffect(() => {
    if (currentTab !== "material Requests") return;

    const activeTab = RequestTabs.find((t) => t.label === activeRequestTab);
    if (!activeTab) {
      setFilteredMaterialRequests(
        sortMaterialRequests(materialRequests, materialRequestSortBy)
      );
      return;
    }

    const filteredAndSorted = sortMaterialRequests(
      materialRequests.filter((request) => activeTab.value.includes(request.status)),
      materialRequestSortBy
    );

    setFilteredMaterialRequests(filteredAndSorted);
  }, [
    activeRequestTab,
    materialRequests,
    currentTab,
    materialRequestSortBy,
    sortMaterialRequests,
  ]);

  useEffect(() => {
    if (!isRequestSortOpen) return;

    const handleClickOutside = (event) => {
      if (requestSortRef.current && !requestSortRef.current.contains(event.target)) {
        setIsRequestSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isRequestSortOpen]);


  const handleFilterChange = useCallback((filteredItems, status) => {
    // Make sure we're sorting filtered deliveries by date too
    const sortedFilteredItems = [...filteredItems].sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });
    setFilteredDeliveries(sortedFilteredItems);
    if (status) {
      setActiveDeliveryStatus(status);
    }
    setCurrentPage(1);
  }, []);

  // Add this useEffect to update totalPages whenever the current items list changes
  useEffect(() => {
    const currentItems = getCurrentItems();
    setTotalPages(Math.ceil(currentItems.length / itemsPerPage));
  }, [
    inventoryItems,
    filteredDeliveries,
    vendorItems,
    machineryItems,
    materialRequests,
    filteredMaterialRequests,
    currentTab,
    itemsPerPage,
  ]);

  const getCurrentItems = () => {
    let items = [];
    switch (currentTab) {
      case "inventory":
        items = inventoryItems;
        break;
      case "Purchases & Deliveries":
        items = filteredDeliveries;
        break;
      case "vendors":
        items = vendorItems;
        break;
      case "machineries":
        items = machineryItems;
        break;
      case "material Requests":
        items = filteredMaterialRequests;
        break;
      default:
        items = [];
    }

    // Apply search filter if searchTerm exists
    if (searchTerm) {
      items = items.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }

    return items;
  };

  const renderItems = () => {
    const items = getCurrentItems();
    const paginatedItems = paginateItems(items);
    // if (isLoading) {
    //   return (
    //     <div className="flex items-center justify-center h-full">
    //       <div className="loader">
    //         <Loader className="w-10 h-10 animate-spin text-purple-500" />
    //       </div>
    //     </div>
    //   );
    // }
    if (paginatedItems.length === 0) {
      return (
        // <div className="text-center py-8 text-gray-500">No items found</div>
        <div className="flex flex-col items-center justify-center py-6">
          <img src={EmptyIcon} alt="No tasks" className="mb-4" width={240} />
          <span className="text-gray-500 text-xl">No items found</span>
        </div>
      );
    }

    switch (currentTab) {
      case "inventory":
        return paginatedItems.map((item, index) => (
          <InventoryItem
            key={item.id || index}
            index={index}
            length={paginatedItems.length}
            item={item}
            onClick={handleItemClick}
            onUpdateClick={(item) => {
              if (
                !userPermissions?.edit_material_management &&
                !userPermissions?.edit_all &&
                !userPermissions?.is_admin
              ) {
                alert.addAlert("You don't have permission to update stock", "error");
                return;
              }
              setStockUpdateItem(item);
            }}
          />
        ));
      case "Purchases & Deliveries":
        return paginatedItems.map((delivery) => (
          <DeliveryCard
            key={delivery.id}
            delivery={delivery}
            onClick={handleDeliveryClick}
          />
        ));
      case "vendors":
        return paginatedItems.map((vendor) => (
          <VendorItem
            key={vendor.id}
            vendor={vendor}
            onClick={handleVendorClick}
          />
        ));
      case "material Requests":
        return paginatedItems.map((request) => (
          <MaterialRequestItem
            key={request.id}
            request={request}
            onClick={handleRequestClick}
            activeRequestTab={activeRequestTab}
          />
        ));
      default:
        return null;
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleItemClick = (item) => {
    navigate(`/site/${siteId}/materials/${item.id}?activeTab=inventory`);
  };

  const handleVendorClick = (vendor) => {
    setSelectedVendor(vendor);
  };

  const handleDeliveryClick = (delivery) => {
    navigate(`/site/${siteId}/materials/${delivery.id}?activeTab=purchase`);
  };

  useEffect(() => {
    if (delivery) {
      setSelectedDelivery(delivery);
      setShowMaterialPurchaseDetails(true);
    }
  }, [delivery]);

  useEffect(() => {
    if (!pendingCreatedRequestId) return;

    const createdRequest = materialRequests.find(
      (request) => String(request.id) === String(pendingCreatedRequestId)
    );
    if (!createdRequest) return;

    setSelectedRequest(createdRequest);
    setPendingCreatedRequestId(null);
  }, [materialRequests, pendingCreatedRequestId]);

  const refreshMaterialRequests = useCallback(async () => {
    const site = localStorage.getItem("site");
    const url = `${BASE_URL}/inventory/material-requests/site/${site}/`;
    const data = await json(url, { method: "GET", body: null });

    if (data && Array.isArray(data.data)) {
      const sortedRequests = [...data.data].sort(
        (a, b) => new Date(b.created_on) - new Date(a.created_on)
      );
      setMaterialRequests(sortedRequests);
      localStorage.setItem(`${site}-materialRequests`, JSON.stringify(sortedRequests));
    }
  }, []);

  const handleMaterialRequestCreated = async (createdResponse) => {
    setCurrentTab("material Requests");
    setActiveRequestTab("Pending");
    setCurrentPage(1);
    setSelectedRequest(null);

    const createdId =
      createdResponse?.id ??
      createdResponse?.data?.id ??
      createdResponse?.data?.request?.id;

    if (createdId != null) {
      setPendingCreatedRequestId(String(createdId));
    }

    await refreshMaterialRequests();
  };

  const paginateItems = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-10 h-10 animate-spin text-purple-500" />
      </div>
    );
  }

  if (
    !userPermissions?.view_material_management &&
    !userPermissions?.is_admin &&
    !userPermissions?.view_all &&
    !permissionsLoading
  ) {
    return (
      <PermissionEmptyState
        module={"Material"}
        description={
          "You don't have permission to view material/inventory information for this site. Please contact your administrator for access. "
        }
      />
    );
  }

  if (showMaterialPurchaseDetails) {
    return (
      <DeliveryDetails
        Delivery={selectedDelivery}
        onClose={() => {
          navigate(`/site/${siteId}/materials/?activeTab=purchase`);
        }}
        setTab={setTab} // ← ✱ forward unchanged
        setDelivery={setDelivery} // ← ✱ forward unchanged
        onMarkReceived={async () => {
          setActiveDeliveryStatus("Delivered");
          await fetchData();
          navigate(`/site/${siteId}/materials/?activeTab=purchase`);
        }}
        onMarkReturned={async () => {
          setActiveDeliveryStatus("Cancelled");
          await fetchData();
          navigate(`/site/${siteId}/materials/?activeTab=purchase`);
        }}
        userPermissions={userPermissions}
      />
    );
  }
  if (selectedItem) {
    return (
      <div className="w-full h-full flex flex-col">
        <InventoryItemDetails
          item={selectedItem}
          userPermissions={userPermissions}
          onBack={() => {
            navigate(`/site/${siteId}/materials/?activeTab=inventory`);
          }}
        />
      </div>
    );
  }

  if (selectedVendor) {
    return (
      <div className="bg-gray-100 w-full h-full flex flex-col p-4">
        <VendorProfile
          vendor={selectedVendor}
          userPermissions={userPermissions}
          onBack={() => setSelectedVendor(null)}
          setTab={setTab}
          setDelivery={setDelivery}
        />
      </div>
    );
  }

  if (showNewDeliveryForm) {
    return (
      <MaterialPurchaseForm
        onClose={() => {
          setShowNewDeliveryForm(false);
          fetchData();
        }}
        onSuccess={fetchData}
      />
    );
  }

  return (
    <LoadingScreen loading={isLoading}>
      <div
        className="bg-gray-100 w-full h-[92vh] flex flex-col p-4"
        style={{
          overflowY: "auto",
        }}
      >
        <ul className="flex flex-row items-center gap-12 mb-4 border-b">
          {["inventory", "Purchases & Deliveries", "material Requests"].map(
            (tab) => (
              <li
                key={tab}
                className={`mr-8 cursor-pointer ${currentTab === tab ? "text-[16px] font-bold" : "text-[16px]"
                  }  pb-2 ${currentTab === tab ? "border-b-2 border-black" : ""}`}
                style={{
                  color: currentTab === tab ? "#232323" : "#718EBF",
                }}
                onClick={() => {
                  const queryTab = reverseTabQueryMap[tab];
                  navigate(`/site/${siteId}/materials/?activeTab=${queryTab}`);
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </li>
            ),
          )}
        </ul>
        {/* {currentTab === "Purchases & Deliveries" && (
          <div className="mb-0 flex justify-between items-center">
            <DeliveryStatusFilter
              deliveries={deliveryItems}
              onFilterChange={handleFilterChange}
              activeStatus={activeDeliveryStatus}
              onActiveStatusChange={setActiveDeliveryStatus}
            />
            <DeliverySortFilter
              deliveries={filteredDeliveries}
              onSortChange={(sortedDeliveries) => {
                setFilteredDeliveries(sortedDeliveries);
                setCurrentPage(1);
              }}
            />
          </div>
        )} */}
        {currentTab === "Purchases & Deliveries" && (
          <div className="mb-0 flex justify-between items-center">
            <DeliveryStatusFilter
              deliveries={deliveryItems}
              onFilterChange={handleFilterChange}
              activeStatus={activeDeliveryStatus}
              onActiveStatusChange={setActiveDeliveryStatus}
            />
          </div>
        )}

        {currentTab === "material Requests" && (
          <div className="mb-0 flex justify-start items-center space-x-4">
            {RequestTabs.map((tab) => (
              <button
                key={tab.label}
                onClick={() => {
                  setActiveRequestTab(tab.label);
                  setCurrentPage(1);
                }}
                className={`px-3 rounded-xl ${activeRequestTab === tab.label
                  ? "bg-[#ECE6F0] text-[#3B0069] border-[1px] border-[#3B0069]"
                  : "bg-[#E3E3E3] text-[#737373]"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end items-center space-x-4 mb-3">
          <Input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearch}
            style={{
              display: `${currentTab === "Purchases & Deliveries" ? "none" : "flex"
                }`,
            }}
            icon={<FaSearch className="text-gray-400" />}
          />
          {currentTab === "inventory" && (
            <InventorySortFilter
              inventoryItems={inventoryItems}
              onSortChange={(sortedItems) => {
                setInventoryItems(sortedItems);
                setCurrentPage(1);
              }}
            />
          )}
          {currentTab === "material Requests" && (
            <div className="relative inline-block" ref={requestSortRef}>
              <button
                className="bg-gray-100 text-gray-700 font-medium px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-gray-200"
                onClick={() => setIsRequestSortOpen((prev) => !prev)}
              >
                <FaSort className="text-gray-600" />
                <span>Sort</span>
                <FaChevronDown
                  className={`text-gray-600 transition-transform ${isRequestSortOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {isRequestSortOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200">
                  {materialRequestSortOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${materialRequestSortBy === option.value
                        ? "bg-purple-50 text-purple-700 font-medium"
                        : "text-gray-700"
                        }`}
                      onClick={() => {
                        setMaterialRequestSortBy(option.value);
                        setCurrentPage(1);
                        setIsRequestSortOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {currentTab !== "Purchases & Deliveries" && (
            <button
              className="flex items-center space-x-2 px-4 py-2 bg-[#3B0069] text-white rounded-lg"
              onClick={() => {
                if (
                  !userPermissions?.edit_material_management &&
                  !userPermissions?.is_admin &&
                  !userPermissions?.edit_all
                ) {
                  alert.addAlert(
                    "You don't have permission to add new material/inventory information for this site. Please contact your administrator for access.",
                    "error",
                  );
                  return;
                }

                if (currentTab === "inventory") {
                  setShowNewMaterialForm(true);
                } else if (currentTab === "vendors") {
                  setIsVendorModalOpen(true);
                } else if (currentTab === "material Requests") {
                  setShowNewRequestForm(true);
                }
              }}
            >
              <FaPlus />
              <span>
                {currentTab === "inventory"
                  ? "New Material"
                  : currentTab === "vendors"
                    ? "New Vendor"
                    : currentTab === "material Requests"
                      ? "New Material Request"
                      : ""}
              </span>
            </button>
          )}
        </div>

        <div
          className={`flex-col gap-2 ${currentTab === "inventory" ? "bg-white" : ""
            } rounded-lg px-2 py-4 ${currentTab === "inventory" ? "h-[65vh]" : "h-[55vh]"} `}
          style={{
            overflowY: "auto",
          }}
        >
          <Card>{renderItems()}</Card>
        </div>

        <div className="flex justify-between absolute bottom-3 w-[75vw] items-center mt-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`py-2 px-4 rounded-md ${currentPage === 1
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-purple-500 text-white hover:bg-purple-600"
              }`}
          >
            Previous
          </Button>

          {/* CUSTOM ITEMS PER PAGE INPUT */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-500">Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="border border-gray-300 rounded-lg p-1"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`py-2 px-4 rounded-md ${currentPage === totalPages
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-purple-500 text-white hover:bg-purple-600"
              }`}
          >
            Next
          </Button>
        </div>

        {showNewMaterialForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <MaterialCreateForm
              onClose={() => {
                setShowNewMaterialForm(false);
                fetchData();
              }}
            />
          </div>
        )}

        {showNewRequestForm && (
          <NewMaterialRequest
            onClose={() => {
              setShowNewRequestForm(false);
            }}
            onCreateMR={handleMaterialRequestCreated}
          />
        )}
        {selectedRequest && (
          <MaterialRequestSidebar
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
          />
        )}
        <NewVendorModal
          isOpen={isVendorModalOpen}
          onClose={() => {
            setIsVendorModalOpen(false);
            fetchData();
          }}
        />
      </div>
      {stockUpdateItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <StockUpdateForm
            onClose={() => {
              setStockUpdateItem(null);
              fetchData();
            }}
            materialId={stockUpdateItem.id}
            currentStock={stockUpdateItem.current_stock}
            onStockUpdate={() => {
              setStockUpdateItem(null);
              fetchData();
            }}
            materialDetails={stockUpdateItem}
          />
        </div>
      )}
    </LoadingScreen>
  );
};

export default Inventory;
